/**
 * WorkflowExceptionActionEditor — action editor for workflow_exceptions context.
 * Supports two mutually exclusive action types:
 *   1. Auto-execute Transitions (existing) — checklist of eligible transitions
 *   2. Require Quote Approval (KAN-169) — people-picker for approver selection
 * Spec: Workflow_Exceptions_Functional_Spec.md Section 7.3,
 *        Request_Quote_Approval_Spec.md Section 11
 * KAN-112, KAN-169
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/api/userService';
import type { UserAdminProfile } from '../../services/api/types';
import type { AutoTransitionKey } from '../../types/workflow';

type ActionType = 'autoTransitions' | 'requiresQuoteApproval';

interface Props {
  action: Record<string, unknown>;
  onChange: (action: Record<string, unknown>) => void;
}

const ELIGIBLE_TRANSITIONS: { key: AutoTransitionKey; labelKey: string; fallback: string }[] = [
  { key: 'booked_to_confirmed', labelKey: 'admin.workflowExceptions.bookedToConfirmed', fallback: 'Booked → Confirmed' },
  { key: 'confirmed_to_contractSigned', labelKey: 'admin.workflowExceptions.confirmedToContractSigned', fallback: 'Confirmed → Contract Signed' },
];

const WorkflowExceptionActionEditor: React.FC<Props> = ({ action, onChange }) => {
  const { t } = useTranslation();

  const [users, setUsers] = useState<UserAdminProfile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Determine current action type from the action object
  const currentActionType: ActionType = action.requiresQuoteApproval
    ? 'requiresQuoteApproval'
    : 'autoTransitions';

  const autoTransitions: string[] = (action.autoTransitions as string[]) || [];
  const approvers: string[] =
    (action.requiresQuoteApproval as any)?.approvers || [];

  // Load users when the component mounts or switches to quote approval mode
  useEffect(() => {
    if (currentActionType === 'requiresQuoteApproval' && users.length === 0) {
      loadUsers();
    }
  }, [currentActionType]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await userService.adminListUsers();
      if (response.success && response.data) {
        // Only show active users
        setUsers(response.data.filter(u => u.status === 'active'));
      }
    } catch {
      // Silently fail — users list will be empty
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleActionTypeChange = (newType: ActionType) => {
    if (newType === 'autoTransitions') {
      onChange({ autoTransitions: [] });
    } else {
      // Load users if not already loaded
      if (users.length === 0) loadUsers();
      onChange({ requiresQuoteApproval: { approvers: [] } });
    }
  };

  const handleToggleTransition = (key: AutoTransitionKey) => {
    const updated = autoTransitions.includes(key)
      ? autoTransitions.filter(k => k !== key)
      : [...autoTransitions, key];
    onChange({ autoTransitions: updated });
  };

  const handleToggleApprover = (uid: string) => {
    const updated = approvers.includes(uid)
      ? approvers.filter(id => id !== uid)
      : [...approvers, uid];
    onChange({ requiresQuoteApproval: { approvers: updated } });
  };

  return (
    <div className="workflow-exception-action-editor">
      {/* Action type radio selector */}
      <div className="action-type-selector">
        <label className="action-type-radio">
          <input
            type="radio"
            name="actionType"
            checked={currentActionType === 'requiresQuoteApproval'}
            onChange={() => handleActionTypeChange('requiresQuoteApproval')}
          />
          <span className="action-type-label">
            {t('admin.workflowExceptions.requireQuoteApproval', 'Require Approval')}
          </span>
        </label>
        <label className="action-type-radio">
          <input
            type="radio"
            name="actionType"
            checked={currentActionType === 'autoTransitions'}
            onChange={() => handleActionTypeChange('autoTransitions')}
          />
          <span className="action-type-label">
            {t('admin.workflowExceptions.autoExecuteTitle', 'Auto-execute Transitions')}
          </span>
        </label>
      </div>

      {/* Auto-execute Transitions */}
      {currentActionType === 'autoTransitions' && (
        <>
          <div className="action-hint">
            {t('admin.workflowExceptions.autoExecuteHint', 'Select which transitions to execute automatically when a matching trade enters the preceding state:')}
          </div>

          <div className="transition-checklist">
            {ELIGIBLE_TRANSITIONS.map(({ key, labelKey, fallback }) => (
              <label key={key} className="transition-checkbox-label">
                <input
                  type="checkbox"
                  checked={autoTransitions.includes(key)}
                  onChange={() => handleToggleTransition(key)}
                />
                <span className="transition-label">{t(labelKey, fallback)}</span>
                <span className="transition-key">({key})</span>
              </label>
            ))}
          </div>

          {autoTransitions.length === 0 && (
            <div className="validation-error">
              {t('admin.workflowExceptions.atLeastOneRequired', 'At least one transition must be selected.')}
            </div>
          )}

          <div className="transition-note">
            {t('admin.workflowExceptions.blockedNote', 'Note: Booking (Draft → Booked) and transitions into Final or Cancelled cannot be auto-executed.')}
          </div>
        </>
      )}

      {/* Require Quote Approval — people picker */}
      {currentActionType === 'requiresQuoteApproval' && (
        <>
          <div className="action-hint">
            {t('admin.workflowExceptions.approverHint', 'Select users who may approve Draft trades matching this rule:')}
          </div>

          {loadingUsers ? (
            <div className="action-hint">{t('common.loading', 'Loading...')}</div>
          ) : users.length === 0 ? (
            <div className="action-hint">{t('admin.workflowExceptions.noUsersFound', 'No active users found.')}</div>
          ) : (
            <div className="approver-checklist">
              {users.map((u) => (
                <label key={u.uid} className="transition-checkbox-label">
                  <input
                    type="checkbox"
                    checked={approvers.includes(u.uid)}
                    onChange={() => handleToggleApprover(u.uid)}
                  />
                  <span className="transition-label">{u.fullName}</span>
                  {u.roleDisplayName && (
                    <span className="transition-key">({u.roleDisplayName})</span>
                  )}
                </label>
              ))}
            </div>
          )}

          {approvers.length === 0 && !loadingUsers && users.length > 0 && (
            <div className="validation-error">
              {t('admin.workflowExceptions.atLeastOneApproverRequired', 'At least one approver must be selected.')}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkflowExceptionActionEditor;
